import type { ROIInputs, ROIResult } from './types';
import { getBenchmark } from './category-benchmarks';

interface ProductForROI {
  pricingMonthly: number;
  avgTimeSavedHrsPerMonth: number;
  avgValueLiftPct: number;
  implementationCost: number;
  category?: string;
  verificationStatus?: string;
  verifiedPricing?: number | null;
}

export function calculateROI(product: ProductForROI, inputs: ROIInputs): ROIResult {
  const isVerified = product.verificationStatus === 'stripe_verified';
  const pricing = isVerified && product.verifiedPricing ? product.verifiedPricing : product.pricingMonthly;

  const benchmark = product.category ? getBenchmark(product.category) : null;
  const hoursSaved = benchmark ? benchmark.avgHoursSavedPerUserMonth : product.avgTimeSavedHrsPerMonth;

  const timeSavingsValue = hoursSaved * inputs.hourlyRate * inputs.teamSize * 12;
  const annualCost = pricing * inputs.teamSize * 12;
  const implementationCost = product.implementationCost || 0;
  const totalCosts = annualCost + implementationCost;
  
  const annualGains = timeSavingsValue;
  const monthlyGains = annualGains / 12;

  const roiPercentage = totalCosts > 0 ? ((annualGains - totalCosts) / totalCosts) * 100 : 0;
  const multiplier = totalCosts > 0 ? Math.round((annualGains / totalCosts) * 10) / 10 : 0;
  const paybackMonths = monthlyGains > 0 ? Math.round((totalCosts / monthlyGains) * 10) / 10 : Infinity;

  return {
    roiPercentage: Math.round(roiPercentage),
    multiplier,
    paybackMonths,
    annualGains: Math.round(annualGains),
    totalCosts: Math.round(totalCosts),
    monthlyGains: Math.round(monthlyGains),
    timeSavingsValue: Math.round(timeSavingsValue),
    revenueLiftValue: 0,
  };
}

/**
 * TrustROI Score = (Verified Paying Customers × Avg Customer Lifetime in months) / Monthly Price per User
 * Returns the score as a number, or null if data is insufficient.
 */
export function calculateTrustROI(product: {
  verified_customer_count?: number | null;
  avg_retention_months?: number | null;
  verified_pricing?: number | null;
  pricing_monthly?: number | null;
}): number | null {
  const users = product.verified_customer_count ?? 0;
  const retention = product.avg_retention_months ?? 0;
  const price = Number(product.verified_pricing || product.pricing_monthly || 0);

  if (users <= 0 || retention <= 0 || price <= 0) return null;

  return Math.round((users * retention) / price * 10) / 10;
}

export function getVerifiedByLabel(verificationStatus: string): string | null {
  const verifiedStatuses = [
    'stripe_verified', 'paddle_verified', 'dodo_verified',
    'revenuecat_verified', 'public_verified', 'admin_seeded',
  ];
  return verifiedStatuses.includes(verificationStatus) ? 'Yes' : null;
}

/** Get displayable TrustROI score: calculated if possible, otherwise fall back to stored trust_score */
export function getDisplayTrustROI(product: {
  verified_customer_count?: number | null;
  avg_retention_months?: number | null;
  verified_pricing?: number | null;
  pricing_monthly?: number | null;
  trust_score?: number | null;
}): number | null {
  const calculated = calculateTrustROI(product);
  if (calculated !== null) return calculated;
  const stored = product.trust_score ? Number(product.trust_score) : null;
  return stored && stored > 0 ? stored : null;
}

/** Check if a product is admin-seeded / manually added */
export function isAdminSeeded(verificationStatus: string): boolean {
  return verificationStatus === 'admin_seeded' || verificationStatus === 'public_verified';
}

/** Display label for user count on manual listings */
export function getUsersDisplay(product: {
  verified_customer_count?: number | null;
  verification_status?: string;
  owner_id?: string | null;
}): { text: string; isValue: boolean } {
  const users = product.verified_customer_count ?? 0;
  if (users > 0) return { text: users.toLocaleString(), isValue: true };
  if (isAdminSeeded(product.verification_status || '')) return { text: 'Hidden', isValue: false };
  if (product.owner_id) return { text: 'Early Stage', isValue: false };
  return { text: '—', isValue: false };
}

/** Display label for retention on manual listings */
export function getRetentionDisplay(product: {
  avg_retention_months?: number | null;
  verification_status?: string;
}): { text: string; isValue: boolean } {
  const retention = product.avg_retention_months ? Number(product.avg_retention_months) : null;
  if (retention !== null && retention > 0) return { text: `${retention}mo`, isValue: true };
  if (isAdminSeeded(product.verification_status || '')) return { text: 'Hidden', isValue: false };
  return { text: '—', isValue: false };
}

export function calculateTrustScore(product: {
  stripeConnected?: boolean;
  verifiedCustomerCount?: number | null;
  monthlyChurnPct?: number | null;
  caseStudyUrl?: string | null;
}): number {
  let score = 20;
  if (product.stripeConnected) score += 40;
  if (product.verifiedCustomerCount && product.verifiedCustomerCount > 100) score += 20;
  if (product.monthlyChurnPct !== null && product.monthlyChurnPct !== undefined && product.monthlyChurnPct < 5) score += 10;
  if (product.caseStudyUrl) score += 10;
  return Math.min(score, 100);
}

export const DEFAULT_ROI_INPUTS: ROIInputs = {
  teamSize: 10,
  hourlyRate: 50,
  annualRevenue: 500000,
  primaryGoal: 'time_savings',
};
